/**
 * MLA (Machine Learning Attribute) footnote construction.
 *
 * Produces a parenthetical note with presentational tokens describing
 * configured MLA filters when a side uses an MLA-based attribute.
 */
import {
  getAllMLAsWithFootnoteStringTokens,
  getLabelRequirementsForFootnoteSentence,
  getMLAFilterComponentLabel,
  getPresentationalValue,
} from '../../mla';
import type { CustomList } from '../../actions/services/customLists';

/**
 * Builds a presentational footnote for the given threshold side (left/right).
 * Returns an empty string when no MLA is present or configuration yields no text.
 */
export function generateMLAFootnoteForSide({
  services,
  actions,
  threshold,
  side,
  customLists,
}: {
  services?: Record<string, any>;
  actions?: Record<string, any>;
  threshold: any;
  side: 'left' | 'right';
  customLists: CustomList[];
}): string {
  try {
    if (!services || !actions) return '';
    const sideData = threshold?.[side];
    if (!sideData?.attributeComponents || sideData.attributeComponents.length === 0) return '';

    const rulesWithMLA = [{
      type: side === 'left' ? 'thresholdLeft' : 'thresholdRight',
      publicId: threshold.publicId,
      data: sideData,
    }];

    const items = getAllMLAsWithFootnoteStringTokens({
      actions: actions as Record<string, any>,
      rulesWithMLA: rulesWithMLA as any,
      services: services as any,
      customLists,
    });

    const mla = items?.[0];
    if (!mla) return '';

    const chunks = mla.presentationalTokens.map((token: any) => {
      const labelRequirements = getLabelRequirementsForFootnoteSentence({
        token,
        attributeName: mla.attributeName,
      });

      if (token.isOptional && !token.value) return '';

      return token.component
        ? getPresentationalValue({
          configuredValue: token.value,
          component: token.component,
          attributeName: mla.attributeName,
          labelRequirements,
        }) || `{${getMLAFilterComponentLabel({ componentData: token.component })}}`
        : token.label;
    });

    const text = chunks.filter(Boolean).join(' ').trim();
    return text ? ` (${text})` : '';
  } catch (e) {
    return '';
  }
}

